// Style engine: two jobs.
//
// 1. styleFit(): how well a candidate matches a declared vibe, 0..1. The
//    plan modules blend this into the score at a capped weight (stylePull),
//    so the vibe STEERS picks without overriding design principles. At the
//    default pull the principles still decide roughly three-quarters of it.
//
// 2. styleInsight(): the threshold-gated "decision insight" layer. It
//    returns STRUCTURED data (axis, direction, magnitude, severity, the raw
//    numbers) and says nothing when a pick fits the vibe. Wording is not its
//    job: core/tone.ts turns this into professional, coach, or roast prose.
//    Same principle as confidence.ts, aimed at taste: surface the
//    significant, suppress the trivial. Two shades of white never flag.

import type { StyleKey, Surface } from "./types.js";
export type { StyleKey } from "./types.js";
export type { Surface } from "./types.js";

// ideal value + tolerance (one "unit" of deviation) per axis.
interface AxisTarget { lrv: number; ltol: number; temp: number; ttol: number; chroma: number; ctol: number; }
interface StyleDef { label: string; blurb: string; wall: AxisTarget; floor: AxisTarget; }

export const STYLES: Record<StyleKey, StyleDef> = {
  scandi: {
    label: "Scandi", blurb: "Light, airy, cool-neutral, minimal pattern.",
    wall:  { lrv: 84, ltol: 12, temp: 0.05, ttol: 0.25, chroma: 0.10, ctol: 0.13 },
    floor: { lrv: 62, ltol: 16, temp: 0.00, ttol: 0.28, chroma: 0.18, ctol: 0.16 },
  },
  "warm-minimal": {
    label: "Warm Minimal", blurb: "Warm neutrals, high light, almost no pattern.",
    wall:  { lrv: 78, ltol: 14, temp: 0.28, ttol: 0.22, chroma: 0.12, ctol: 0.13 },
    floor: { lrv: 58, ltol: 18, temp: 0.30, ttol: 0.24, chroma: 0.16, ctol: 0.16 },
  },
  "modern-farmhouse": {
    label: "Modern Farmhouse", blurb: "Warm, bright walls, characterful floors.",
    wall:  { lrv: 75, ltol: 16, temp: 0.30, ttol: 0.26, chroma: 0.15, ctol: 0.16 },
    floor: { lrv: 48, ltol: 22, temp: 0.30, ttol: 0.30, chroma: 0.30, ctol: 0.22 },
  },
  moody: {
    label: "Moody", blurb: "Deep, enveloping, low light, rich contrast.",
    wall:  { lrv: 35, ltol: 20, temp: 0.05, ttol: 0.35, chroma: 0.35, ctol: 0.25 },
    floor: { lrv: 22, ltol: 16, temp: 0.10, ttol: 0.30, chroma: 0.32, ctol: 0.24 },
  },
  coastal: {
    label: "Coastal", blurb: "Cool, breezy, light, blue-green leaning.",
    wall:  { lrv: 76, ltol: 14, temp: -0.05, ttol: 0.24, chroma: 0.18, ctol: 0.16 },
    floor: { lrv: 64, ltol: 18, temp: -0.05, ttol: 0.26, chroma: 0.20, ctol: 0.18 },
  },
  contemporary: {
    label: "Contemporary", blurb: "Neutral, controlled contrast, low pattern.",
    wall:  { lrv: 68, ltol: 22, temp: 0.05, ttol: 0.30, chroma: 0.15, ctol: 0.16 },
    floor: { lrv: 50, ltol: 24, temp: 0.05, ttol: 0.30, chroma: 0.20, ctol: 0.20 },
  },
  transitional: {
    label: "Transitional", blurb: "Warm-neutral, forgiving, broadly appealing.",
    wall:  { lrv: 70, ltol: 22, temp: 0.20, ttol: 0.30, chroma: 0.20, ctol: 0.20 },
    floor: { lrv: 52, ltol: 24, temp: 0.20, ttol: 0.30, chroma: 0.24, ctol: 0.22 },
  },
};

export function styleLabel(key: StyleKey): string {
  return STYLES[key]?.label ?? key;
}

export interface FinishAttrs {
  name: string;
  lrv: number;
  temperature: number; // effective (post-light) temperature, -1..1
  chroma: number; // busyness: chroma_opinion for paint, grain/veining for floors
}

export type InsightAxis = "lightness" | "temperature" | "chroma";
export type InsightDirection = "darker" | "brighter" | "cooler" | "warmer" | "busier" | "flatter";
export type InsightSeverity = "notable" | "strong";
export type InsightMagnitude = "a bit" | "noticeably" | "a lot";

export interface StyleInsight {
  axis: InsightAxis;
  direction: InsightDirection;
  severity: InsightSeverity;
  magnitude: InsightMagnitude;
  dev: number; // signed deviation in tolerance units
  name: string;
  value: number; // the pick's value on the axis (LRV for lightness)
  target: number; // the vibe's target on that axis
  styleLabel: string;
}

// Default significance threshold. Below this, no insight is produced.
export const INSIGHT_THRESHOLD = 1.0;

function axisDevs(attrs: FinishAttrs, t: AxisTarget) {
  return [
    { axis: "lightness" as InsightAxis, dev: (attrs.lrv - t.lrv) / t.ltol, value: attrs.lrv, target: t.lrv },
    { axis: "temperature" as InsightAxis, dev: (attrs.temperature - t.temp) / t.ttol, value: attrs.temperature, target: t.temp },
    { axis: "chroma" as InsightAxis, dev: (attrs.chroma - t.chroma) / t.ctol, value: attrs.chroma, target: t.chroma },
  ];
}

// 0..1 fit used to STEER a pick. 1 = dead-on the vibe; falls off with the
// RMS deviation across the axes. Not thresholded: steering wants a smooth
// gradient, not a cliff. `ignoreLightness` drops the lightness axis, used
// when the user has pinned an explicit LRV target so the vibe steers only
// temperature and chroma, not the lightness they already chose.
export function styleFit(
  attrs: FinishAttrs, styleKey: StyleKey, surface: Surface, ignoreLightness = false
): number {
  const style = STYLES[styleKey];
  if (!style) return 0.5;
  const devs = axisDevs(attrs, style[surface]).filter((d) => !(ignoreLightness && d.axis === "lightness"));
  const rms = Math.sqrt(devs.reduce((s, d) => s + d.dev * d.dev, 0) / devs.length);
  return Math.max(0, 1 - rms / 2.5);
}

function directionFor(axis: InsightAxis, dev: number): InsightDirection {
  if (axis === "lightness") return dev < 0 ? "darker" : "brighter";
  if (axis === "temperature") return dev < 0 ? "cooler" : "warmer";
  return dev > 0 ? "busier" : "flatter";
}

function magnitudeFor(absDev: number): InsightMagnitude {
  if (absDev >= 1.8) return "a lot";
  if (absDev >= 1.3) return "noticeably";
  return "a bit";
}

// Return structured insight data only if the largest axis deviation clears
// the threshold; otherwise null (the pick fits the vibe, say nothing).
export function styleInsight(
  attrs: FinishAttrs,
  styleKey: StyleKey,
  surface: Surface,
  threshold: number = INSIGHT_THRESHOLD
): StyleInsight | null {
  const style = STYLES[styleKey];
  if (!style) return null;

  const devs = axisDevs(attrs, style[surface]);
  let top = devs[0];
  for (const d of devs) if (Math.abs(d.dev) > Math.abs(top.dev)) top = d;
  if (Math.abs(top.dev) < threshold) return null;

  return {
    axis: top.axis,
    direction: directionFor(top.axis, top.dev),
    severity: Math.abs(top.dev) >= 1.8 ? "strong" : "notable",
    magnitude: magnitudeFor(Math.abs(top.dev)),
    dev: +top.dev.toFixed(2),
    name: attrs.name,
    value: +top.value.toFixed(2),
    target: +top.target.toFixed(2),
    styleLabel: style.label,
  };
}
